import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { ShieldCheck, ShieldX, ArrowRight } from "lucide-react";
import CalsideMark from "@/components/CalsideMark";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); } catch { return "—"; }
}

export default function Verify() {
  const { certId } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        // public endpoint — no credentials needed
        const { data } = await axios.get(`${API_BASE}/verify/${certId}`);
        setState({ loading: false, data });
      } catch (e) {
        setState({ loading: false, error: e.response?.status === 404 ? "not_found" : "error" });
      }
    })();
  }, [certId]);

  return (
    <div className="min-h-screen bg-brand-bone">
      <header className="border-b border-brand-linen">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <CalsideMark size={36} />
            <div className="leading-tight">
              <div className="font-serif text-xl text-brand-ink">Calside</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Certificate verification</div>
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        {state.loading && <div className="text-brand-muted">Checking certificate…</div>}

        {state.error === "not_found" && (
          <div className="bg-white border border-brand-linen rounded-3xl p-10 shadow-card text-center">
            <ShieldX className="w-10 h-10 text-brand-accent mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif text-4xl text-brand-ink mt-6">Certificate not found</h1>
            <p className="text-brand-body mt-3">This link is invalid or the certificate has been revoked. Please contact the practice manager.</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-8 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover">
              Go to Calside <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        )}

        {state.error === "error" && (
          <div className="bg-white border border-brand-linen rounded-3xl p-10 text-center">
            <h1 className="font-serif text-3xl text-brand-ink">Verification unavailable</h1>
            <p className="text-brand-body mt-2">Please try again in a moment.</p>
          </div>
        )}

        {state.data && (() => {
          const d = state.data;
          const valid = d.is_valid;
          return (
            <div className={`bg-white border-2 rounded-3xl p-10 shadow-card ${valid ? "border-brand-ink" : "border-brand-accent"}`}>
              <div className="flex items-center gap-3">
                {valid ? (
                  <>
                    <ShieldCheck className="w-10 h-10 text-brand-ink" strokeWidth={1.5} />
                    <div>
                      <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">Verified</div>
                      <h1 className="font-serif text-3xl text-brand-ink">This certificate is valid.</h1>
                    </div>
                  </>
                ) : (
                  <>
                    <ShieldX className="w-10 h-10 text-brand-accent" strokeWidth={1.5} />
                    <div>
                      <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">Expired</div>
                      <h1 className="font-serif text-3xl text-brand-accent">This certificate has expired.</h1>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-8 pt-8 border-t border-brand-linen space-y-5">
                {d.logo_url && (
                  <div>
                    <img src={d.logo_url} alt={`${d.practice_name} logo`} onError={(e) => { e.currentTarget.style.display = "none"; }}
                      className="h-14 object-contain rounded-lg" />
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Issued by</div>
                  <div className="font-serif text-2xl text-brand-ink mt-1">{d.practice_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Learner</div>
                  <div className="font-serif text-2xl text-brand-ink mt-1">{d.learner_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Course</div>
                  <div className="font-serif text-xl text-brand-ink mt-1">{d.course_title}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-muted">Score</div>
                    <div className="font-serif text-xl text-brand-ink mt-1">{d.quiz_score}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-muted">Issued</div>
                    <div className="font-serif text-lg text-brand-ink mt-1">{fmtDate(d.issued_at)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-muted">Valid until</div>
                    <div className={`font-serif text-lg mt-1 ${valid ? "text-brand-ink" : "text-brand-accent"}`}>{d.expires_at ? fmtDate(d.expires_at) : "No expiry"}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Certificate ID</div>
                  <div className="font-mono text-xs text-brand-secondary break-all mt-1">{d.certificate_id}</div>
                </div>
                <div className="pt-4 border-t border-brand-linen text-xs text-brand-muted italic">
                  Suitable evidence for CPD records and training portfolios. This certificate is issued by Calside and is not endorsed by any external regulator.
                </div>
              </div>
            </div>
          );
        })()}
      </main>
      <footer className="border-t border-brand-linen mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-sm text-brand-muted">
          Certificates are issued by Calside (practice training for UK general practice), created by Grant Cameron Anthony, on completion of a final assessment (pass mark 70%). Contact the issuing practice for further evidence.
        </div>
      </footer>
    </div>
  );
}
